import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { grantAnalyticsConsent, denyAnalyticsConsent } from "@/lib/analytics";
import { grantPixelConsent, denyPixelConsent } from "@/lib/metaPixel";
import { isNativeApp } from "@/lib/nativeApp";

const STORAGE_KEY = "cookie-consent";

/**
 * Returns true only if the user has explicitly accepted cookies.
 * Use this guard before loading any analytics, tracking pixels, or
 * third-party scripts that set non-essential cookies (GDPR compliance).
 *
 * Example:
 *   if (hasConsentedToCookies()) { loadGoogleAnalytics(); }
 */
export const hasConsentedToCookies = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "accepted";
  } catch {
    return false;
  }
};

/** Reset cookie consent so the banner reappears. */
export const resetCookieConsent = (): void => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  window.dispatchEvent(new Event('cookie-consent-reset'));
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation(["common"]);
  const location = useLocation();
  const lp = useLocalizedPath();
  const isDesigner = location.pathname.includes('/cctv-design');
  // The client portal (/p/:token) is a white-label page the installer's
  // CLIENT sees — our cookie banner must not overlay their branding.
  const isClientPortal = /^\/p\//.test(location.pathname);

  useEffect(() => {
    try {
      const consent = localStorage.getItem(STORAGE_KEY);
      if (!consent) {
        const timer = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(timer);
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  // Listen for reset events (triggered from footer "Cookie Settings" link)
  useEffect(() => {
    const handler = () => setVisible(true);
    window.addEventListener('cookie-consent-reset', handler);
    return () => window.removeEventListener('cookie-consent-reset', handler);
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, "accepted"); } catch { /* ignore */ }
    grantAnalyticsConsent();
    grantPixelConsent();
    setVisible(false);
  };

  const decline = () => {
    try { localStorage.setItem(STORAGE_KEY, "declined"); } catch { /* ignore */ }
    denyAnalyticsConsent();
    denyPixelConsent();
    setVisible(false);
  };

  // The store app has no consent banner: it loads no analytics or ad tags
  // (main.tsx, index.html), so there is nothing to consent to and no App
  // Store tracking prompt is needed.
  if (!visible || isDesigner || isClientPortal || isNativeApp()) return null;

  return (
    <div
      role="dialog"
      aria-label={t("cookie_eyebrow", "§ / COOKIE POLICY")}
      className="fixed bottom-0 left-0 right-0 z-[100] py-3 px-4 md:py-3 md:px-12"
      style={{
        background: 'var(--color-page-bg)',
        borderTop: '1px solid var(--color-gray-border-strong)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)',
      }}
    >
      <div className="pelco-container grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:gap-6 items-center">
        <div className="flex flex-col gap-1 md:gap-2">
          <p
            className="text-[13px] md:text-[15px]"
            style={{
              fontFamily: 'var(--font-body)',
              lineHeight: 1.5,
              color: 'var(--color-gray-dark)',
              maxWidth: '65ch',
            }}
          >
            {/* Short on phones: the full sentence was the largest text on many
                pages, so painting the banner became the page's LCP. */}
            <span className="md:hidden">{t("cookie_message_short", "Analytics cookies only if you accept.")}</span>
            <span className="hidden md:inline">{t("cookie_message", "We use cookies to analyze site usage and improve your experience. You can accept or decline non-essential cookies.")}</span>{" "}
            <Link
              to={lp("/rodo")}
              className="uppercase underline underline-offset-2"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '11px',
                letterSpacing: '0.14em',
                color: 'var(--color-gray-dark)',
              }}
            >
              {t("privacy_policy", "Privacy Policy")}
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-6 md:justify-end">
          <button
            type="button"
            onClick={decline}
            className="uppercase"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.14em',
              color: 'var(--color-gray-dark)',
              background: 'transparent',
              border: 0,
              borderBottom: '1px solid var(--color-gray-muted)',
              padding: '0.25rem 0',
              cursor: 'pointer',
              transition: 'border-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderBottomColor = 'var(--color-gray-dark)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderBottomColor = 'var(--color-gray-muted)';
            }}
          >
            {t("cookie_decline_cta", "DECLINE")}
          </button>
          <button
            type="button"
            onClick={accept}
            className="uppercase"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.14em',
              background: 'var(--color-gray-dark)',
              color: 'var(--color-page-bg)',
              padding: '0.875rem 1.5rem',
              border: 0,
              borderRadius: 0,
              cursor: 'pointer',
            }}
          >
            {t("cookie_accept_cta", "ACCEPT →")}
          </button>
        </div>
      </div>
    </div>
  );
};
